import { useEffect, useState } from 'react';

/**
 * @param condition
 * @param interval
 */
export type PollConfigCondition = {
  condition: boolean;
  interval: number;
};

/**
 * @param defaultInterval
 * @param conditions
 */
type PollConfig = {
  defaultInterval: number;
  conditions: PollConfigCondition[];
};

export type UseDynamicPolling = {
  stopPolling: () => void;
  startPolling: (interval: number) => void;
  pollConfig: PollConfig;
};

export type UseDynamicPollingControls = {
  pausePolling: () => void;
  resumePolling: () => void;
};

/**
 * Dynamically adjust polling rate based on configurations provided
 * @param stopPolling
 * @param startPolling
 * @param pollConfig polling configuration, provides default polling rate and conditions for new polling rate
 */
export const useDynamicPolling = ({
  stopPolling,
  startPolling,
  pollConfig,
}: UseDynamicPolling): UseDynamicPollingControls => {
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) {
      return stopPolling();
    }

    const interval = getInterval(pollConfig);
    startPolling(interval);

    return () => {
      stopPolling();
    };
  }, [pollConfig, startPolling, stopPolling, isPaused]);

  return {
    pausePolling: () => setIsPaused(true),
    resumePolling: () => setIsPaused(false),
  };
};

// Use the interval of the first truthy condition, otherwise, use the default
const getInterval = (pollConfig: PollConfig): number => {
  const customInterval = pollConfig.conditions.find((entry) => entry.condition)
    ?.interval;
  return customInterval || pollConfig.defaultInterval;
};
